import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useProjectPhaseDetail } from './hooks/usePhases';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  planned: 'bg-blue-50 text-blue-500',
  ready: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  blocked: 'bg-red-100 text-red-600',
  qa_pending: 'bg-indigo-100 text-indigo-600',
  completed: 'bg-emerald-100 text-emerald-600',
  handover_pending: 'bg-teal-100 text-teal-600',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-slate-400 text-white',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  planned: 'Lên kế hoạch',
  ready: 'Sẵn sàng',
  in_progress: 'Đang triển khai',
  blocked: 'Tạm dừng',
  qa_pending: 'Chờ nghiệm thu',
  completed: 'Hoàn thành',
  handover_pending: 'Chờ bàn giao',
  closed: 'Đã đóng',
  cancelled: 'Đã hủy',
};

const typeLabels: Record<string, string> = {
  site_preparation: 'Chuẩn bị mặt bằng',
  earthwork_grading: 'San nền / Tạo địa hình',
  soil_preparation: 'Xử lý đất trồng',
  irrigation_installation: 'Hệ thống tưới',
  lighting_installation: 'Đèn cảnh quan',
  hardscape_edge: 'Bó vỉa / Hardscape',
  tree_planting: 'Trồng cây bóng mát',
  shrub_groundcover: 'Cây bụi / Phủ nền',
  turf_lawn: 'Trải cỏ',
  fertilizing_mulching: 'Phân bón / Phủ gốc',
  testing_commissioning: 'Vận hành thử (T&C)',
  defect_rectification: 'Sửa lỗi',
  handover: 'Bàn giao',
};

export default function PhaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { phase: item, isLoading, updatePhase } = useProjectPhaseDetail(id);

  const [activeTab, setActiveTab] = useState('overview');

  if (isLoading) return <div className="p-12 text-center text-slate-500 sm:text-sm text-xs">Đang tải chi tiết giai đoạn...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy thông tin giai đoạn</div>;

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updatePhase({ status_code: newStatus as any });
      toast.success(`Đã cập nhật trạng thái giai đoạn sang ${statusLabels[newStatus]}`);
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'boq', label: 'Khối lượng BOQ' },
    { value: 'zones', label: 'Khu vực liên quan' },
    { value: 'work_orders', label: 'Lệnh việc (Work Orders)' },
    { value: 'progress', label: 'Nhật ký tiến độ' },
    { value: 'qa', label: 'Nghiệm thu' },
    { value: 'finance', label: 'Chi phí & Thanh toán' },
  ];

  return (
    <PageDetailLayout
      title={item.name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold`}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="sm:text-sm text-xs text-slate-400 font-mono">#{item.code}</span>
          <span className="text-slate-300">|</span>
          <span className="sm:text-sm text-xs text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {item.project?.name}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Giai đoạn dự án', path: '/projects/phases' },
        { label: item.code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {item.status_code === 'qa_pending' && (
            <AuthButton module="projects" action="update" variant="success" icon="ri-check-line" onClick={() => handleStatusChange('completed')}>
              Hoàn thành Giai đoạn
            </AuthButton>
          )}
          <AuthButton module="projects" action="update" variant="outline" icon="ri-edit-line">
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Tiến độ thực tế" value={item.progress_percent} icon="ri-line-chart-line" variant="primary" suffix="%" />
                <KPICard title="Dự kiến hoàn thành" value={item.planned_end_date ? new Date(item.planned_end_date).toLocaleDateString('vi-VN') : '---'} icon="ri-calendar-event-line" variant="info" />
                <KPICard title="Đặc tính mốc" value={item.payment_milestone ? 'THANH TOÁN' : 'THI CÔNG'} icon="ri-flag-line" variant="warning" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Mô tả giai đoạn
                </h3>
                <p className="sm:text-sm text-xs text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                  "{item.description || 'Chưa có thông tin mô tả chi tiết cho giai đoạn này.'}"
                </p>

                <div className="mt-8">
                  <h4 className="text-[10px] font-bold text-slate-400 mb-4 tracking-widest">Tiến độ chi tiết</h4>
                  <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                    <div className="h-full bg-os-primary transition-all shadow-[0_0_12px_rgba(var(--os-primary-rgb),0.3)]" style={{ width: `${item.progress_percent}%` }} />
                  </div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-400">
                    <span>BẮT ĐẦU: {item.actual_start_date ? new Date(item.actual_start_date).toLocaleDateString('vi-VN') : 'CHƯA CHẠY'}</span>
                    <span className="text-os-primary">{item.progress_percent}% HOÀN THÀNH</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-list-check-line text-slate-400" />
                  Các yêu cầu chốt giai đoạn
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-2xl border ${item.qa_required ? 'bg-indigo-50 border-indigo-100 text-indigo-700' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                    <i className="ri-shield-check-line mb-2 block text-lg" />
                    <p className="sm:text-sm text-xs font-normal">Nghiệm thu QA</p>
                    <p className="text-[10px] opacity-70">{item.qa_required ? 'Bắt buộc' : 'Không bắt buộc'}</p>
                  </div>
                  <div className={`p-4 rounded-2xl border ${item.handover_required ? 'bg-teal-50 border-teal-100 text-teal-700' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                    <i className="ri-key-line mb-2 block text-lg" />
                    <p className="sm:text-sm text-xs font-normal">Bàn giao (Handover)</p>
                    <p className="text-[10px] opacity-70">{item.handover_required ? 'Bắt buộc' : 'Không bắt buộc'}</p>
                  </div>
                  <div className={`p-4 rounded-2xl border ${item.payment_milestone ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                    <i className="ri-money-dollar-circle-line mb-2 block text-lg" />
                    <p className="sm:text-sm text-xs font-normal">Thanh toán (Billing)</p>
                    <p className="text-[10px] opacity-70">{item.payment_milestone ? 'Mốc thanh toán' : 'Nội bộ'}</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-slate-900 text-white">
                <h4 className="font-bold mb-4 flex items-center gap-2 text-os-primary">
                  <i className="ri-history-line" />
                  Kế hoạch Thời gian
                </h4>
                <div className="space-y-4">
                  <div className="relative pl-6 border-l border-slate-700 pb-4">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-blue-500" />
                    <p className="text-[10px] text-slate-400 font-bold">Ngày bắt đầu kế hoạch</p>
                    <p className="sm:text-sm text-xs font-normal text-white">{item.planned_start_date ? new Date(item.planned_start_date).toLocaleDateString('vi-VN') : '---'}</p>
                  </div>
                  <div className="relative pl-6 border-l border-slate-700">
                    <div className="absolute left-[-5px] top-0 w-2.5 h-2.5 rounded-full bg-slate-600" />
                    <p className="text-[10px] text-slate-400 font-bold">Ngày kết thúc kế hoạch</p>
                    <p className="sm:text-sm text-xs font-normal text-white">{item.planned_end_date ? new Date(item.planned_end_date).toLocaleDateString('vi-VN') : '---'}</p>
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-800">
                  <Alert variant="warning" className="bg-amber-900/30 border-amber-800 text-amber-200 sm:text-sm text-xs">
                    Giai đoạn đang chậm 3 ngày so với kế hoạch ban đầu.
                  </Alert>
                </div>
              </Card>

              <Card className="p-6">
                <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-node-tree text-slate-400" />
                  Phân cấp & Phụ thuộc
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Phân loại:</span>
                    <span className="sm:text-sm text-xs font-normal text-slate-700">{typeLabels[item.phase_type]}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="sm:text-sm text-xs text-slate-500">Giai đoạn cha:</span>
                    <span className="sm:text-sm text-xs font-normal text-os-primary underline">{item.parent_phase?.name || '---'}</span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab !== 'overview' && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được liên kết từ các phân hệ BOQ khối lượng, Lệnh việc thực địa, QA Nghiệm thu và Kế toán dự án."
          />
        )}
      </div>
    </PageDetailLayout>
  );
}
